/**
 * The decimal separator that Base UI's NumberField parses for `locale`. Pass the
 * same `locale` as the field Root; `undefined` means the browser locale.
 */
export const getDecimalSeparator = (locale?: Intl.LocalesArgument): string => {
  const decimalPart = new Intl.NumberFormat(locale)
    .formatToParts(1.1)
    .find((part) => part.type === 'decimal')

  return decimalPart?.value ?? '.'
}

export type TypedDecimalSeparatorResult = 'kept' | 'rejected'

/**
 * Base UI removes the locale GROUP separator before it parses, so "0.13" typed in
 * de-DE becomes 13 and "0,13" typed in en-US becomes 13. Users type either key
 * as the decimal, so this rewrites the one character just typed to the locale
 * decimal separator. It never touches the rest of the text, because a formatted
 * value such as "1.500" in de-DE holds real group separators.
 *
 * Returns `'rejected'` after it removes the character because the text already
 * holds a decimal separator. The caller must then stop Base UI from reading the
 * input.
 */
export const applyTypedDecimalSeparator = (
  input: HTMLInputElement,
  nativeEvent: Event,
  locale: Intl.LocalesArgument | undefined,
): TypedDecimalSeparatorResult => {
  // Mobile keypads often send keydown with `key: "Unidentified"`, so only the
  // input event carries the real character.
  if (!(nativeEvent instanceof InputEvent) || nativeEvent.inputType !== 'insertText') {
    return 'kept'
  }

  const typed = nativeEvent.data
  if (typed !== '.' && typed !== ',') {
    return 'kept'
  }

  const caret = input.selectionStart
  if (caret === null || input.value[caret - 1] !== typed) {
    return 'kept'
  }

  const before = input.value.slice(0, caret - 1)
  const after = input.value.slice(caret)
  const separator = getDecimalSeparator(locale)

  if (`${before}${after}`.includes(separator)) {
    input.value = `${before}${after}`
    input.setSelectionRange(before.length, before.length)

    return 'rejected'
  }

  if (typed !== separator) {
    input.value = `${before}${separator}${after}`
    input.setSelectionRange(before.length + separator.length, before.length + separator.length)
  }

  return 'kept'
}
